'use client';

import React, { useState, useEffect } from 'react';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { ImageUploadDropzone } from '@/components/ui/ImageUploadDropzone';
import { ServiceItem, CategoryItem } from './ServicesCategoryAccordion';
import {
  Scissors,
  Clock,
  DollarSign,
  Users,
  Sparkles,
  ShieldCheck,
  AlertCircle
} from 'lucide-react';

export interface ServiceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  service: ServiceItem | null;
  modality?: 'CLASICO' | 'PAUSA' | 'ZONAS' | 'CONSULTA';
  categories: CategoryItem[];
  staffList: { id: string; name: string; colorHex: string; roleTitle?: string }[];
  onSaved: () => void;
  onOpenNewCategory?: () => void;
}

export function ServiceDrawer({
  isOpen,
  onClose,
  service,
  modality = 'CLASICO',
  categories,
  staffList,
  onSaved,
  onOpenNewCategory
}: ServiceDrawerProps) {
  const [activeTab, setActiveTab] = useState<'general' | 'pricing' | 'staff'>('general');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [cashPrice, setCashPrice] = useState('15000');
  const [listPrice, setListPrice] = useState('17500');
  const [depositRequired, setDepositRequired] = useState(false);
  const [depositType, setDepositType] = useState('fixed');
  const [depositAmount, setDepositAmount] = useState('5000');
  const [duration, setDuration] = useState(45);
  const [bufferAfter, setBufferAfter] = useState(5);
  const [serviceModality, setServiceModality] = useState<'CLASICO' | 'PAUSA' | 'ZONAS' | 'CONSULTA'>(modality);
  const [pauseDuration, setPauseDuration] = useState(0);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [active, setActive] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (service) {
      setName(service.name);
      setDescription(service.description || '');
      setCategoryId(service.categoryId || (categories[0]?.id ?? ''));
      setImageUrl(service.imageUrl || '');
      setCashPrice(service.cashPrice?.toString() || '15000');
      setListPrice(service.listPrice?.toString() || '17500');
      setDepositRequired(Boolean(service.depositRequired));
      setDepositType(service.depositType || 'fixed');
      setDepositAmount(service.depositAmount?.toString() || '5000');
      setDuration(service.duration || 45);
      setBufferAfter(service.bufferAfter ?? 5);
      setServiceModality((service.modality as any) || modality || 'CLASICO');
      setPauseDuration(service.pauseDuration || 0);
      setSelectedStaffIds(service.staff ? service.staff.map((s) => s.id) : staffList.map((s) => s.id));
      setActive(service.active !== undefined ? service.active : true);
      setIsFeatured(Boolean(service.isFeatured));
    } else {
      setName('');
      setDescription('');
      setCategoryId(categories[0]?.id ?? '');
      setImageUrl('');
      setCashPrice('15000');
      setListPrice('17500');
      setDepositRequired(false);
      setDepositType('fixed');
      setDepositAmount('5000');
      setDuration(45);
      setBufferAfter(5);
      setServiceModality(modality || 'CLASICO');
      setPauseDuration(0);
      setSelectedStaffIds(staffList.map((s) => s.id));
      setActive(true);
      setIsFeatured(false);
    }
    setError(null);
    setActiveTab('general');
  }, [service, isOpen, categories, staffList, modality]);

  const toggleStaffSelection = (staffId: string) => {
    setSelectedStaffIds((prev) =>
      prev.includes(staffId) ? prev.filter((id) => id !== staffId) : [...prev, staffId]
    );
  };

  const selectAllStaff = () => {
    setSelectedStaffIds(staffList.map((s) => s.id));
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('El nombre del servicio es obligatorio');
      setActiveTab('general');
      return;
    }

    const cash = parseFloat(cashPrice) || 0;
    const list = parseFloat(listPrice) || cash;
    const dep = parseFloat(depositAmount) || 0;

    setLoading(true);
    setError(null);

    const payload = {
      id: service ? service.id : undefined,
      name: name.trim(),
      description: description.trim() || null,
      categoryId: categoryId || null,
      imageUrl: imageUrl || null,
      duration: Number(duration) || 30,
      bufferAfter: Number(bufferAfter) || 0,
      cashPrice: cash,
      listPrice: list,
      depositRequired,
      depositType,
      depositAmount: dep,
      modality: serviceModality,
      pauseDuration: Number(pauseDuration) || 0,
      staffIds: selectedStaffIds,
      active,
      isFeatured
    };

    try {
      const url = '/api/services';
      const method = service ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (data.success) {
        onSaved();
        onClose();
      } else {
        setError(data.error || 'Error al guardar el servicio');
      }
    } catch (err: any) {
      setError('Error de conexión con el servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SlideOverDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={service ? 'Editar Servicio' : 'Nuevo Servicio'}
      subtitle={service ? `Modificando "${service.name}"` : 'Completá la información del catálogo'}
      footer={
        <div className="flex items-center justify-between w-full">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={loading}>
            {service ? 'Guardar Cambios' : 'Crear Servicio'}
          </Button>
        </div>
      }
    >
      {/* Error alert */}
      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-2 text-rose-700 text-xs font-medium">
          <AlertCircle className="w-4 h-4 shrink-0 text-rose-500" />
          <span>{error}</span>
        </div>
      )}

      {/* Tabs de Navegación */}
      <div className="flex items-center border-b border-slate-150 gap-2">
        <button
          type="button"
          onClick={() => setActiveTab('general')}
          className={`pb-2.5 px-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${
            activeTab === 'general'
              ? 'border-[#6255F5] text-[#6255F5]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          General y Tiempos
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('pricing')}
          className={`pb-2.5 px-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${
            activeTab === 'pricing'
              ? 'border-[#6255F5] text-[#6255F5]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Precios y Seña
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('staff')}
          className={`pb-2.5 px-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${
            activeTab === 'staff'
              ? 'border-[#6255F5] text-[#6255F5]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Profesionales ({selectedStaffIds.length})
        </button>
      </div>

      {/* TAB 1: General y Tiempos */}
      {activeTab === 'general' && (
        <div className="space-y-4">
          <Input
            label="Nombre del Servicio *"
            placeholder="Ej: Corte Degradé Clásico, Keratina, Barba"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!name.trim() && error ? 'El nombre es obligatorio' : undefined}
          />

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-slate-700">Categoría</label>
              {onOpenNewCategory && (
                <button
                  type="button"
                  onClick={onOpenNewCategory}
                  className="text-xs text-[#6255F5] font-semibold hover:underline cursor-pointer"
                >
                  + Nueva categoría
                </button>
              )}
            </div>
            <Select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Descripción o Instrucciones
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detalles sobre el procedimiento, productos usados o recomendaciones..."
              className="w-full p-3 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#6255F5] focus:ring-2 focus:ring-[#6255F5]/20 placeholder:text-slate-400"
            />
          </div>

          {/* Duración y Colchón de Limpieza */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <Input
              label="Duración Activa (min)"
              type="number"
              step="5"
              min="5"
              max="480"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              leftIcon={<Clock className="w-4 h-4" />}
              helperText="Tiempo que dura la atención"
            />

            <Input
              label="Buffer Post-Servicio (min)"
              type="number"
              step="5"
              min="0"
              max="60"
              value={bufferAfter}
              onChange={(e) => setBufferAfter(Number(e.target.value))}
              helperText="Tiempo de limpieza / descanso"
            />
          </div>

          {/* Modalidad de servicio */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Modalidad de Servicio
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'CLASICO', label: 'Clásico', desc: 'Atención continua' },
                { id: 'PAUSA', label: 'Con Pausa', desc: 'Espera en salón (ej: tintura)' }
              ].map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setServiceModality(m.id as any)}
                  className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                    serviceModality === m.id
                      ? 'border-[#6255F5] bg-[#F4F3FF] text-[#6255F5]'
                      : 'border-slate-200 hover:border-slate-300 text-slate-700'
                  }`}
                >
                  <p className="text-xs font-bold">{m.label}</p>
                  <p className="text-[10px] text-slate-500">{m.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {serviceModality === 'PAUSA' && (
            <Input
              label="Tiempo de Pausa / Exposición (min)"
              type="number"
              step="5"
              min="5"
              value={pauseDuration}
              onChange={(e) => setPauseDuration(Number(e.target.value))}
              helperText="Durante este lapso el profesional puede atender a otro cliente"
            />
          )}

          {/* Foto del Servicio */}
          <div className="pt-2">
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Imagen Ilustrativa (Opcional)
            </label>
            <ImageUploadDropzone
              value={imageUrl}
              onChange={setImageUrl}
              aspect="video"
              label="Foto del servicio"
            />
          </div>
        </div>
      )}

      {/* TAB 2: Precios y Política de Seña */}
      {activeTab === 'pricing' && (
        <div className="space-y-4">
          <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 text-xs text-slate-600 space-y-1">
            <p className="font-semibold text-slate-800">Precios Duales de Calenddy</p>
            <p>
              Ofrecé un precio especial en efectivo / transferencia y un precio de lista para tarjetas.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Precio en Efectivo ($) *"
              type="number"
              step="100"
              min="0"
              value={cashPrice}
              onChange={(e) => setCashPrice(e.target.value)}
              leftIcon={<DollarSign className="w-4 h-4 text-emerald-600" />}
              helperText="Precio de contado"
            />

            <Input
              label="Precio de Lista ($)"
              type="number"
              step="100"
              min="0"
              value={listPrice}
              onChange={(e) => setListPrice(e.target.value)}
              leftIcon={<DollarSign className="w-4 h-4 text-slate-400" />}
              helperText="Tarjetas o cuotas"
            />
          </div>

          {/* Política de Señas */}
          <div className="p-4 bg-white border border-slate-200 rounded-2xl space-y-3 mt-4">
            <Switch
              checked={depositRequired}
              onChange={setDepositRequired}
              label="Exigir Seña Obligatoria para Agendar"
              description="El cliente deberá abonar una seña por Mercado Pago para confirmar la cita"
            />

            {depositRequired && (
              <div className="pt-3 border-t border-slate-100 space-y-3 animate-fade-in">
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    label="Tipo de Seña"
                    value={depositType}
                    onChange={(e) => setDepositType(e.target.value)}
                    options={[
                      { value: 'fixed', label: 'Monto Fijo ($)' },
                      { value: 'percentage', label: 'Porcentaje (%)' }
                    ]}
                  />

                  <Input
                    label={depositType === 'percentage' ? 'Porcentaje (%)' : 'Monto Seña ($)'}
                    type="number"
                    min="1"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    leftIcon={depositType === 'fixed' ? <DollarSign className="w-4 h-4" /> : undefined}
                    helperText={
                      depositType === 'percentage'
                        ? `Equivale a $${Math.round(((parseFloat(cashPrice) || 0) * (parseFloat(depositAmount) || 0)) / 100).toLocaleString('es-AR')}`
                        : 'Monto a debitar'
                    }
                  />
                </div>
              </div>
            )}
          </div>

          {/* Switches de Visibilidad */}
          <div className="space-y-3 pt-2">
            <Switch
              checked={active}
              onChange={setActive}
              label="Servicio Activo en Catálogo"
              description="Visible para reservas públicas en tu portal web"
            />

            <Switch
              checked={isFeatured}
              onChange={setIsFeatured}
              label="Destacar en Portal Público"
              description="Aparece con medalla de recomendación en la cabecera"
            />
          </div>
        </div>
      )}

      {/* TAB 3: Profesionales Asignados */}
      {activeTab === 'staff' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500 font-medium">
              Seleccioná qué miembros del equipo pueden realizar este servicio:
            </p>
            <button
              type="button"
              onClick={selectAllStaff}
              className="text-xs text-[#6255F5] font-semibold hover:underline cursor-pointer"
            >
              Marcar todos
            </button>
          </div>

          <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white">
            {staffList.map((st) => {
              const isSelected = selectedStaffIds.includes(st.id);

              return (
                <div
                  key={st.id}
                  onClick={() => toggleStaffSelection(st.id)}
                  className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${
                    isSelected ? 'bg-[#F4F3FF]/40' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      style={{ backgroundColor: st.colorHex || '#10b981' }}
                      className="w-8 h-8 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0 shadow-xs"
                    >
                      {st.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900">{st.name}</p>
                      {st.roleTitle && (
                        <p className="text-[11px] text-slate-400">{st.roleTitle}</p>
                      )}
                    </div>
                  </div>

                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}} // Manejado por onClick del contenedor
                    className="w-4 h-4 rounded text-[#6255F5] accent-[#6255F5] cursor-pointer"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </SlideOverDrawer>
  );
}
